import React from "react";
import PropTypes from "prop-types";
import {
  Modal,
  Box,
  Card,
  CardContent,
  CardActions,
  IconButton,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

/**
 * Shared modal frame that mirrors the design used in VehicleDetailModal.
 * Wraps content inside a gradient header, elevated card, and sticky footer actions.
 */
const ModalLayout = ({
  open,
  onClose,
  title,
  subtitle,
  headerContent,
  children,
  actions,
  maxWidth = 780,
  containerSx,
  headerSx,
  contentSx,
  footerSx,
  cardProps,
  modalProps,
}) => {
  return (
    <Modal open={open} onClose={onClose} {...modalProps}>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: "90%",
          maxWidth,
          maxHeight: "90vh",
          outline: "none",
          ...containerSx,
        }}
      >
        <Card
          sx={{
            borderRadius: 3,
            boxShadow: 8,
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            maxHeight: "90vh",
            position: "relative",
          }}
          {...cardProps}
        >
          <IconButton
            onClick={onClose}
            sx={{ position: "absolute", top: 8, right: 8, zIndex: 10 }}
          >
            <CloseIcon />
          </IconButton>

          <CardContent sx={{ p: 0, display: "flex", flexDirection: "column", flex: 1 }}>
            <Box
              sx={{
                px: 4,
                py: 3,
                background: "linear-gradient(135deg, #d3f8df 0%, #f4fff7 100%)",
                borderBottom: "1px solid #e5e7eb",
                ...headerSx,
              }}
            >
              {subtitle && (
                <Typography variant="subtitle2" color="text.secondary">
                  {subtitle}
                </Typography>
              )}
              {title && (
                <Typography variant="h5" fontWeight={700}>
                  {title}
                </Typography>
              )}
              {headerContent}
            </Box>

            <Box
              sx={{
                px: 4,
                py: 3,
                flex: 1,
                overflowY: "auto",
                backgroundColor: "#fff",
                ...contentSx,
              }}
            >
              {children}
            </Box>
          </CardContent>

          {actions && (
            <CardActions
              sx={{
                borderTop: "1px solid #e5e7eb",
                p: 3,
                justifyContent: "flex-end",
                flexWrap: "wrap",
                gap: 1.5,
                backgroundColor: "#fff",
                ...footerSx,
              }}
            >
              {actions}
            </CardActions>
          )}
        </Card>
      </Box>
    </Modal>
  );
};

ModalLayout.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  title: PropTypes.string,
  subtitle: PropTypes.string,
  headerContent: PropTypes.node,
  children: PropTypes.node,
  actions: PropTypes.node,
  maxWidth: PropTypes.number,
  containerSx: PropTypes.object,
  headerSx: PropTypes.object,
  contentSx: PropTypes.object,
  footerSx: PropTypes.object,
  cardProps: PropTypes.object,
  modalProps: PropTypes.object,
};

export default ModalLayout;
